import React, { FC, useEffect, useState, ChangeEvent, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';

import handleKeyPress from '../../../common/handleKeyPress';
import Input from '../../../componentLibrary/Input';
import { triggerFetch, resetPosts } from '../../../actions/thirdPartyContent';
import HorizontalRule from '../../../componentLibrary/HorizontalRule';
import Button from '../../../componentLibrary/Button';
import { TAppState } from '../../../reducers';
import { IBlock } from '../../../types/content';
import { ISBPost, ISBConfig, TRenderLoadingOption } from '../../../types/thirdPartyContent';
import Create from './Create';
import List from './List';
import imageSources from '../../../constants/imageSources/imageSources';
import rules from '../../../constants/businessRules/businessRules';
import { setApplicationFocus } from '../../../actions/browser';
import APPLICATION_FOCUS from '../../../constants/applicationFocus';
import * as Styled from './styles';
import Preferences from './Preferences';

interface IProps {
    block: IBlock;
    dispatch: Dispatch<any>;
    loadingConfig: boolean;
    loadingPosts: boolean;
    isLazyLoading: boolean;
    posts: ISBPost[];
    staffbaseUrl: string;
    config: ISBConfig | null;
    resetLazyLoading: (query?: string) => void;
    applicationFocus: string;
}

const StaffbaseSettings: FC<IProps> = (props) => {
    const {
        block,
        dispatch,
        loadingPosts,
        isLazyLoading,
        loadingConfig,
        posts,
        staffbaseUrl,
        config,
        resetLazyLoading,
        applicationFocus,
    } = props;
    const [searchInput, setSearchInput] = useState('');
    const [renderOption, setRenderOption] = useState<TRenderLoadingOption>('posts');
    const [currentLanguage, setCurrentLanguage] = useState<string>(config?.defaultLanguage || '');
    const searchRef = useRef<string>();

    useEffect(() => {
        if (applicationFocus === APPLICATION_FOCUS.RIGHT_PANEL) {
            dispatch(setApplicationFocus(APPLICATION_FOCUS.NONE));
        }
    }, [applicationFocus, dispatch]);

    useEffect(() => {
        // used to access searchInput value during unmount cleanup, refetch posts if unmounting with search value active
        searchRef.current = searchInput;
    }, [searchInput]);

    const filterPosts = useCallback(
        _debounce((input: string) => {
            dispatch(
                triggerFetch('posts', {
                    query: input,
                    limit: !input ? rules.THIRD_PARTY_SB_POSTS_LOAD_BATCH_SIZE.toString() : '100',
                })
            );
            resetLazyLoading(input);
        }, 500),
        []
    );

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const input = event.currentTarget.value.toLowerCase();
        setSearchInput(input);
        filterPosts(input.trim());
    };

    useEffect(() => () => filterPosts.cancel(), []);

    const refreshPosts = () => {
        setSearchInput('');
        dispatch(resetPosts(null));
        dispatch(triggerFetch('posts', { limit: rules.THIRD_PARTY_SB_POSTS_LOAD_BATCH_SIZE.toString() }));
        resetLazyLoading();
    };

    useEffect(() => {
        if (posts.length === 0) {
            dispatch(triggerFetch('posts', { limit: rules.THIRD_PARTY_SB_POSTS_LOAD_BATCH_SIZE.toString() }));
            resetLazyLoading();
        }
        return () => {
            if (searchRef.current) {
                dispatch(triggerFetch('posts', { limit: rules.THIRD_PARTY_SB_POSTS_LOAD_BATCH_SIZE.toString() }));
                resetLazyLoading();
            }
            dispatch(resetPosts(null));
        };
    }, []);

    useEffect(() => {
        if (posts.length === 0 && !loadingPosts) {
            !searchInput ? setRenderOption('create') : setRenderOption('noPosts');
        } else if (isLazyLoading) {
            // do not show loading when lazy loading
            setRenderOption('posts');
        } else {
            setRenderOption('posts');
        }
    }, [posts, searchInput, loadingPosts]);

    const renderSwitch = (option: TRenderLoadingOption) => {
        if (!config) {
            return null;
        }
        switch (option) {
            case 'create':
                return <Create staffbaseUrl={staffbaseUrl} />;
            case 'posts':
                return (
                    <>
                        <Input id="sb-settings-search" type="text" icon="search" placeholder="Search" onChange={onChange} value={searchInput} />
                        <div>
                            <Styled.PreferencesButton
                                onClick={() => setRenderOption('preferences')}
                                onKeyDown={(e) => handleKeyPress(e, () => setRenderOption('preferences'))}
                            >
                                <Styled.SlidersIcon icon={['far', 'sliders-h']} />
                                View options
                            </Styled.PreferencesButton>
                        </div>
                        {posts.length > 0 && (
                            <List
                                block={block}
                                posts={posts}
                                loadingPosts={loadingPosts}
                                currentLanguage={currentLanguage}
                            />
                        )}
                        {loadingPosts && (
                            <Styled.LoadingWrapper postsLength={posts.length}>
                                <img src={imageSources.CONTENT_LIBRARY_LOADER} alt="Loading" width="110" />
                            </Styled.LoadingWrapper>
                        )}
                    </>
                );
            case 'preferences':
                return (
                    <Preferences
                        setRenderOption={setRenderOption}
                        setCurrentLanguage={setCurrentLanguage}
                        languages={config.languages}
                        defaultLanguage={config.defaultLanguage}
                    />
                );
            case 'noPosts':
                return (
                    <>
                        <Input id="sb-settings-search-no-posts" type="text" icon="search" placeholder="Search" onChange={onChange} value={searchInput} />
                        <div>
                            <Styled.PreferencesButton
                                onClick={() => setRenderOption('preferences')}
                                onKeyDown={(e) => handleKeyPress(e, () => setRenderOption('preferences'))}
                            >
                                <Styled.SlidersIcon icon={['far', 'sliders-h']} />
                                View options
                            </Styled.PreferencesButton>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <img src={imageSources.STAFFBASE.NO_RESULTS} alt="" width="130" />
                            <Styled.Text>Hmm...we couldn&apos;t find any posts with this name.</Styled.Text>
                        </div>
                    </>
                );
            default:
                return <Create staffbaseUrl={staffbaseUrl} />;
        }
    };

    if (loadingConfig) {
        return (
            <Styled.LoadingWrapper postsLength={posts.length}>
                <img src={imageSources.CONTENT_LIBRARY_LOADER} alt="Loading" width="110" />
            </Styled.LoadingWrapper>
        );
    }

    return (
        <Styled.StaffbaseSettings>
            <Styled.Header>
                <span style={{ marginTop: 2 }}>RECENTLY PUBLISHED</span>
                <div style={{ position: 'relative', left: 2 }}>
                    <Button
                        type="simple"
                        icon={['far', 'sync']}
                        iconSize="12"
                        width={25}
                        color="#424242"
                        marginBottom="0px"
                        padding={0}
                        label="Refresh posts"
                        dataTestId="refresh-posts"
                        onClick={refreshPosts}
                        isFocused={applicationFocus === APPLICATION_FOCUS.RIGHT_PANEL}
                    />
                </div>
            </Styled.Header>
            <HorizontalRule />
            {renderSwitch(renderOption)}
        </Styled.StaffbaseSettings>
    );
};

const mapStateToProps = (state: TAppState) => ({
    loadingConfig: state.thirdPartyContent.staffbase.loadingConfig,
    loadingPosts: state.thirdPartyContent.staffbase.loadingPosts,
    isLazyLoading: state.thirdPartyContent.staffbase.isLazyLoading,
    posts: state.thirdPartyContent.staffbase.posts as ISBPost[],
    config: state.thirdPartyContent.staffbase.config as ISBConfig,
    staffbaseUrl: _get(state, 'thirdPartyContent.staffbase.config.studioUrl', 'https://staffbase.com'),
    applicationFocus: state.browser.applicationFocus,
});

export default connect(mapStateToProps)(StaffbaseSettings);
